import { Address, IActionMenuBadgeItem, Invoice, ProviderGuarantor, renderAmount, ToastContext } from '@enter/core';
import { mdiEmail, mdiHome, mdiPhone } from '@mdi/js';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { DialogContext, ErrorDialog } from 'services';
import { parseError } from 'services/dialog/util';
import { RootStore } from 'stores';
import { CollectionPaymentDialog } from './collection-payment-dialog';
import { GuarantorDocumentDialog } from './guarantor-document-dialog';
import * as S from './guarantor-header.styles';
import { showRecordDiscountDialog } from './show-record-discount-dialog';

interface IGuarantorHeaderProps {
    claimId: string;
    guarantor?: ProviderGuarantor;
    patientInvoice?: Invoice;
}

export const GuarantorHeader = inject('rootStore')(
    observer((props: IGuarantorHeaderProps) => {
        const rootStore: RootStore = _.get(props, 'rootStore');
        if (!rootStore) {
            return <></>;
        }
        const { claimStore } = rootStore;
        const { claimId, guarantor, patientInvoice } = props;

        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);

        const renderAddress = (address: Address, i: number): React.ReactElement => (
            <S.GuarantorInfo key={`address-${i}`}>
                <S.GuarantorIcon path={mdiHome} />
                {_.join(address.lines, ',')}
                <br />
                {address.city}, {address.state} {address.postal}
            </S.GuarantorInfo>
        );

        const payBill = async (): Promise<void> => {
            if (guarantor?.id) {
                try {
                    const shortUrl = await claimStore.getGuarantorCheckoutUrl(guarantor.id);
                    if (shortUrl && shortUrl.url) {
                        window.open(shortUrl.url, '_blank');
                    }
                } catch (err) {
                    const parsedError = await parseError(err);
                    if (parsedError.payload.code === 'err.billing_not_active_for_invoice') {
                        dialogContext.showDialog({
                            props: {
                                onClose: handleCloseDialog,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Billing is not active for this invoice yet',
                            },
                            Component: (
                                <ErrorDialog
                                    onClose={handleCloseDialog}
                                    payload={{
                                        message: 'If you feel this is in error, please reach out to support.',
                                        hideTrace: true,
                                    }}
                                />
                            ),
                        });
                    } else if (parsedError.payload.code === 'err.customer_portal_not_active') {
                        dialogContext.showDialog({
                            props: {
                                onClose: handleCloseDialog,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Customer portal is not active',
                            },
                            Component: (
                                <ErrorDialog
                                    onClose={handleCloseDialog}
                                    payload={{
                                        message:
                                            'Customer portal is not available for this customer, because they have never been billed. If you feel this is in error, please reach out to support.',
                                        hideTrace: true,
                                    }}
                                />
                            ),
                        });
                    } else {
                        dialogContext.showError(err);
                    }
                }
            }
        };

        const viewBill = async (): Promise<void> => {
            try {
                if (guarantor && guarantor.id) {
                    const tempUrl = await claimStore.getGuarantorViewBillUrl(guarantor.id);
                    if (tempUrl && tempUrl.url) {
                        window.open(tempUrl.url, '_blank');
                        return;
                    }
                }
            } catch (err) {
                dialogContext.showError(err);
            }
            toastContext.notify?.('Billing document is currently unavailable.', 'right');
        };

        const viewAllBills = async (): Promise<void> => {
            if (patientInvoice) {
                dialogContext.showDialog({
                    props: {
                        onClose: handleCloseDialog,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: 'View all bills',
                    },
                    Component: (
                        <GuarantorDocumentDialog onClose={handleCloseDialog} claimId={claimId} guarantor={guarantor} />
                    ),
                });
            }
        };

        const addLineItem = async (): Promise<void> => {
            if (patientInvoice) {
                showRecordDiscountDialog({ dialogContext, toastContext, claimStore, claimId, invoice: patientInvoice });
            }
        };

        const handleCloseDialog = () => {
            dialogContext.hideDialog();
        };

        const recordCollectionPayment = (): void => {
            dialogContext.showDialog({
                props: {
                    onClose: handleCloseDialog,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: 'Record Payment',
                },
                Component: <CollectionPaymentDialog onClose={handleCloseDialog} claimId={claimId} />,
            });
        };

        // const recordRefundPayment = (): void => {
        //     dialogContext.showDialog({
        //         props: {
        //             onClose: handleCloseDialog,
        //             disableBackdropClick: true,
        //             disableEscapeKeyDown: true,
        //             renderTitle: 'Record Payment',
        //         },
        //         Component: (
        //             <CollectionPaymentDialog onClose={handleCloseDialog} claimId={claimId} paymentType="REFUND" />
        //         ),
        //     });
        // };

        const guarantorItems = (): IActionMenuBadgeItem[] => [
            'divider',
            {
                label: 'Pay Bill',
                action: payBill,
            },
            {
                label: 'View Bill',
                action: viewBill,
            },
            {
                label: 'View All Bills',
                action: viewAllBills,
            },
            {
                label: 'Set Discount Amount',
                action: addLineItem,
            },
            {
                label: 'Record Collection Payment',
                action: recordCollectionPayment,
            },
            // {
            //     label: 'Refund Payment',
            //     action: recordRefundPayment,
            // },
        ];

        if (!guarantor) {
            return <S.StyledRow />;
        }
        const addressRows = _.map(guarantor.addresses, (address, i) => renderAddress(address, i));
        return (
            <S.StyledRow>
                <S.StyledCol>
                    <S.StyledRow>
                        <S.GuarantorName>
                            {guarantor.first} {guarantor.middle} {guarantor.last}{' '}
                            {guarantor.suffix ? `, ${guarantor.suffix}` : ''} (Guarantor)
                        </S.GuarantorName>
                    </S.StyledRow>
                    <S.StyledRow>
                        {guarantor.phone && (
                            <S.GuarantorInfo>
                                <S.GuarantorIcon path={mdiPhone} />
                                {guarantor.phone}
                            </S.GuarantorInfo>
                        )}
                        {guarantor.email && (
                            <S.GuarantorInfo>
                                <S.GuarantorIcon path={mdiEmail} />
                                {guarantor.email}
                            </S.GuarantorInfo>
                        )}
                        {addressRows}
                    </S.StyledRow>
                </S.StyledCol>
                <S.Spacer />
                <S.StyledCol>
                    <S.GuarantorAmount>
                        {patientInvoice && (
                            <>
                                {renderAmount(patientInvoice.paidAmount)} / {renderAmount(patientInvoice.amount)}
                            </>
                        )}
                    </S.GuarantorAmount>
                    <S.StyledCol>
                        <S.ActionBadge
                            id={guarantor.id}
                            first={guarantor.first}
                            last={guarantor.last}
                            items={guarantorItems()}
                        />
                    </S.StyledCol>
                </S.StyledCol>
            </S.StyledRow>
        );
    }),
);
